const defaultRadius = 8;
const defaultCircleness = 30;
const defaultInset = 0.5;

/**
 * Generates a clip path for a squircle.
 * 
 * @param {*} radius Pixels or percent, eg. "16px" or "30%". Percentage values must be between 0 and 50%.
 * @param {*} c Percentage between 0 and 100.
 * 0% will yield minimum circleness (will decrease the amount of curvature to the rounded borders).
 * 100% will yield fully curved borders! (a circle when border radius is set to 50%)
 * 
 * @param {*} inset Inset in pixels
 * @param {*} width Width of the element to squirclify
 * @param {*} height Height of the element to squirclify
 * @returns 
 */
function getSquirclePath (radius = defaultRadius, c = defaultCircleness, inset = defaultInset, width, height) {
  if (! radius) {
    radius = defaultRadius; 
  }
  if (! c) {
    c = defaultCircleness;
  }
  if (! inset) {
    inset = defaultInset;
  }
  let rx1 = 0, rx2 = 0, ry1 = 0, ry2 = 0;
  const x1 = inset;
  const y1 = inset;
  width = width  - (inset * 2);
  height = height - (inset * 2);
  let x2 = width + x1;
  let y2 = height + y1;
  
  if (typeof radius === 'string' && radius.indexOf('%')) {
    radius = parseInt(radius.replace("%", ''));
    // Clamp value between 0 and 50.
    radius = radius > 50 ? 50 : radius;
    rx1 = (radius / 100) * width; // Left corner x position.
    rx2 = width - rx1; // Right corner x position.
    ry1 = (radius / 100) * height; // Top corner y position
    ry2 = height - ry1; // Bottom corner y position.
  } else {
    if (typeof radius === 'string' && radius.indexOf('px')) {
      radius = parseInt(radius.replace('px'));
    }
    if (typeof radius === 'number') {
      rx1 = radius;
      rx2 = width - rx1;
      ry1 = radius;
      ry2 = height - ry1;
    }
  }
  
  const circleness = c > 100 ? 100 : c;
  // A factor of 0.45 at border radius 50% will give an almost perfect circle. Therefore the factor
  // is capped at 0.45;
  const circlenessFactor = circleness * 0.0045;

  let cx1 = rx1 * circlenessFactor; // Left control point x position.
  let cx2 = width - cx1; // Right control point x position.
  let cy1 = ry1 * circlenessFactor;
  let cy2 = height - cy1;
  
  rx1 += x1;
  rx2 += x1;
  cx1 += x1;
  cx2 += x1;
  ry1 += y1;
  ry2 += y1;
  cy1 += y1;
  cy2 += y1;

  let path = `"M ${rx1},${y1} C ${cx1},${y1} ${x1},${cy1} ${x1},${ry1} L ${x1},${ry2} C ${x1},${cy2} ${cx1},${y2} ${rx1},${y2} L ${rx2},${y2} C ${cx2},${y2} ${x2},${cy2} ${x2},${ry2} L ${x2},${ry1} C ${x2},${cy1} ${cx2},${y1} ${rx2},${y1} Z"`;

  return path;
}

function updateSquirclePathIfNeeded (el, binding) {
  if (binding.value === false) { return; } // early exit on v-squircle="false"



  if (el.prevWidth !== el.offsetWidth || el.prevHeight !== el.offsetHeight) {
    el.style.cssText += 'clip-path: path(' + 
      getSquirclePath(
        binding.value?.radius,
        binding.value?.circleness,
        binding.value?.inset,
        el.offsetWidth,
        el.offsetHeight
      ) + ');';

    el.prevWidth = el.offsetWidth;
    el.prevHeight = el.offsetHeight;
  } 
}

const squircle = {
  onResize (el, binding) {
    updateSquirclePathIfNeeded(el, binding);
  },
  // called when the bound element's parent component
  // and all its children are mounted.
  mounted(el, binding) {
    updateSquirclePathIfNeeded(el, binding);
  },

  // called after the parent component and
  // all of its children have updated
  updated (el, binding) {
    updateSquirclePathIfNeeded(el, binding);
  },
}

export default squircle;

export {
  updateSquirclePathIfNeeded
};